import { PipelineRepository, StepTask } from '@telespot/domain';
import { LocalizedName } from '@protocols/domain';

import { LabelNamesDto } from '../../dtos/label-names.dto';
import type { ILabelNamesPort } from '../../ports/label-names.port';

export interface RetrievePipelineStepCompatibilityOutput {
  id: string;
  outputLabels: Set<string>;
  outputLabelNames: Record<string, LocalizedName>;
  task: StepTask;
}

export interface IRetrievePipelineStepCompatibilityQuery {
  /**
   * Retrieves a single pipeline step by id, projected to the fields needed
   * for compatibility checks (task plus the labels emitted by the step, with
   * their localized names). Repository errors propagate so the caller can
   * update its error state.
   */
  execute(pipelineStepId: string): Promise<RetrievePipelineStepCompatibilityOutput>;
}

export class RetrievePipelineStepCompatibilityQuery implements IRetrievePipelineStepCompatibilityQuery {
  constructor(
    private readonly pipelineRepository: PipelineRepository,
    private readonly labelNamesPort: ILabelNamesPort
  ) {}

  public async execute(pipelineStepId: string): Promise<RetrievePipelineStepCompatibilityOutput> {
    const step = await this.pipelineRepository.getStepById(pipelineStepId);
    return {
      id: step.id,
      outputLabels: step.outputLabels,
      outputLabelNames: await this.resolveLabelNames(Array.from(step.outputLabels)),
      task: step.task,
    };
  }

  /**
   * The port omits uuids it found no label for; this caller wants every output
   * label named, so it fills the gaps with the uuid itself. The read is shared,
   * the fallback is not — a compatibility check needs something to display for
   * a label that no longer exists.
   */
  private async resolveLabelNames(uuids: string[]): Promise<LabelNamesDto> {
    // Copied before filling: the map now comes from a collaborator, and the
    // fallback is this caller's own view of the data, not the port's.
    const namesByUuid: LabelNamesDto = { ...(await this.labelNamesPort.resolve(uuids)) };
    for (const uuid of uuids) {
      namesByUuid[uuid] ??= { en: uuid };
    }
    return namesByUuid;
  }
}
